import { ScrollingModule } from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from "rxjs";
import { BookingService } from "../booking.service";

export interface VenuePlayerOption {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
}

@Component({
  selector: "app-venue-player-picker",
  standalone: true,
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    ScrollingModule,
  ],
  templateUrl: "./venue-player-picker.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenuePlayerPickerComponent implements OnChanges, OnDestroy {
  @Input() venueId: string | null = null;
  @Input() excludedPlayerIds: string[] = [];
  @Input() disabled = false;
  @Output() playerSelected = new EventEmitter<VenuePlayerOption>();
  @ViewChild(MatAutocompleteTrigger) trigger?: MatAutocompleteTrigger;

  readonly searchControl = new FormControl("", { nonNullable: true });
  players: VenuePlayerOption[] = [];
  loading = false;
  hasNextPage = false;
  private page = 0;
  private requestVersion = 0;
  private activeRequest?: Subscription;
  private readonly pageSize = 30;
  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly bookingService: BookingService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.searchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroyed))
      .subscribe(() => this.reload());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["venueId"]) this.reload();
    if (changes["disabled"]) {
      this.disabled
        ? this.searchControl.disable({ emitEvent: false })
        : this.searchControl.enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.activeRequest?.unsubscribe();
    this.destroyed.next();
    this.destroyed.complete();
  }

  open(): void {
    if (!this.venueId || this.disabled) return;
    if (!this.players.length && !this.loading) this.loadNextPage();
    queueMicrotask(() => this.trigger?.openPanel());
  }

  loadWhenNearEnd(renderedIndex: number): void {
    if (renderedIndex + 10 >= this.players.length) this.loadNextPage();
  }

  select(event: MatAutocompleteSelectedEvent): void {
    const player = event.option.value as VenuePlayerOption;
    if (!player?._id || this.isExcluded(player._id)) return;
    this.playerSelected.emit(player);
    this.searchControl.setValue("", { emitEvent: false });
    this.reload();
  }

  clear(): void {
    this.searchControl.setValue("");
    this.open();
  }

  isExcluded(playerId: string): boolean {
    return this.excludedPlayerIds.includes(playerId);
  }

  private reload(): void {
    this.activeRequest?.unsubscribe();
    this.activeRequest = undefined;
    this.loading = false;
    this.requestVersion += 1;
    this.page = 0;
    this.players = [];
    this.hasNextPage = false;
    if (this.venueId) this.loadNextPage();
    this.cdr.markForCheck();
  }

  private loadNextPage(): void {
    if (!this.venueId || this.loading || (this.page > 0 && !this.hasNextPage)) return;
    const version = this.requestVersion;
    const nextPage = this.page + 1;
    this.loading = true;
    this.activeRequest = this.bookingService
      .getVenuePlayers(this.venueId, this.searchControl.value, nextPage, this.pageSize)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (response) => {
          if (version !== this.requestVersion) return;
          const incoming = Array.isArray(response?.docs) ? response.docs : [];
          const known = new Set(this.players.map((player) => player._id));
          this.players = [...this.players, ...incoming.filter((player: VenuePlayerOption) => !known.has(player._id))];
          this.page = nextPage;
          this.hasNextPage = response?.hasNextPage === true;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (version !== this.requestVersion) return;
          this.loading = false;
          this.hasNextPage = false;
          this.cdr.markForCheck();
        },
      });
  }
}
